import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { groupBy, sortBy, take } from 'lib/array/arrayUtils'
import useCurrentLoyaltyActivities from 'loyalty/hooks/useCurrentLoyaltyActivities'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import LoyaltyAccountLandingPageUserActivity from './LoyaltyAccountLandingPageUserActivity'
import Caption from 'components/Luxkit/Typography/Caption'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import TextLink from 'components/Luxkit/TextLink'
import useToggle from 'hooks/useToggle'
import Typography from 'components/Luxkit/Typography/Typography'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import LineMinusIcon from 'components/Luxkit/Icons/line/LineMinusIcon'
import LineRedoIcon from 'components/Luxkit/Icons/line/LineRedoIcon'

const Section = styled(Pane)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(48)} ${rem(24)};
  }
`

const TableHeader = styled(Pane)`
  display: grid;
  grid-template-columns: 1fr ${rem(72)} ${rem(72)};
  padding: ${rem(4)} ${rem(16)};
`

function LoyaltyAccountLandingPageUserActivities() {
  const [activities, fetchingActivities] = useCurrentLoyaltyActivities()
  const [pendingPoints, fetchingPendingPoints] = useCurrentLoyaltyPendingPoints()
  const [showMore, toggleShowMore] = useToggle(false)

  const allActivities = useMemo(() => {
    // pending points are also 'activities', so lets add them to the list
    const pointActivities = pendingPoints.items.map((pending): App.LoyaltyAccountActivity => ({
      basePoints: pending.points,
      date: pending.createdAt,
      id: pending.id,
      statusCredits: pending.statusCredits,
      type: 'pending_points',
      orderId: pending.orderId,
      orderItemId: pending.orderItemId,
    }))
    return sortBy(activities.concat(pointActivities), activity => new Date(activity.date), 'desc')
  }, [activities, pendingPoints.items])

  const activitesByYear = useMemo(() => {
    let visibleCount = showMore ? allActivities.length : 5
    if (!showMore && allActivities.length === 6) {
      // it's silly to make them 'show more' for one record, so give it an extra
      // but if it's > 6, the 'show more' should show and only 5 records
      visibleCount = 6
    }
    const groups = groupBy(take(allActivities, visibleCount), activity => new Date(activity.date).getFullYear())

    return groups.values().map(group => sortBy(group, activity => new Date(activity.date), 'desc')).toArray()
  }, [allActivities, showMore])

  const fetching = fetchingPendingPoints || fetchingActivities

  return <Section>
    <VerticalSpacer gap={16}>
      <Heading variant="heading3">Your activity</Heading>
      {fetching && <LoadingIndicator inline/>}
      {!fetching && <>
        {activities.length === 0 && <BodyText variant="large">
          You’re on your way to great rewards. Your transactions for earning and using points,{' '}
          as well as redeeming benefits will appear here when you make bookings.
        </BodyText>}
        {allActivities.length > 0 && <VerticalSpacer gap={12}>
          <Group direction="horizontal" gap={12} horizontalAlign="end">
            <CaptionTextBlock variant="medium" startIcon={<LinePlusIcon colour="success" />}>
              Points earned
            </CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineMinusIcon colour="critical" />}>
              Points used
            </CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineRedoIcon colour="warning" />}>
              Points pending
            </CaptionTextBlock>
          </Group>
          <VerticalSpacer gap={20}>
            {activitesByYear.map(activities => <div key={activities[0].date}>
              <TableHeader type="light-grey">
                <Caption variant="large" weight="bold" colour="neutral-three">
                  {new Date(activities[0].date).getFullYear()}
                </Caption>
                <BodyText variant="small" colour="neutral-two" weight="bold" align="end">Points</BodyText>
                <BodyText variant="small" colour="neutral-two" weight="bold" align="end">Status</BodyText>
              </TableHeader>
              {activities.map(activity => <LoyaltyAccountLandingPageUserActivity
                key={activity.id}
                activity={activity}
              />)}
            </div>)}
          </VerticalSpacer>
        </VerticalSpacer>}
        {allActivities.length > 6 && <Typography align="center">
          <TextLink size="caption" onClick={toggleShowMore}>
            Show {showMore ? 'less' : 'more'}
          </TextLink>
        </Typography>}
        <BodyText variant="small" colour="neutral-two" align="center">
          Please note some transactions may take up to 24hrs to appear after your booking is completed
        </BodyText>
      </>}
    </VerticalSpacer>
  </Section>
}

export default LoyaltyAccountLandingPageUserActivities
